/**
 * DelegatePreview - Admin preview mode for delegate/attendee experience
 * Allows admins to see exactly what delegates see without logging out
 */
import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { eventAPI, adminDelegateAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

// Feature Components
import SafetyCenter from '@/components/SafetyCenter';
import VenueMaps from '@/components/VenueMaps';
import { getToken } from '@/lib/authToken';

// Icons
import {
  MapPin, Utensils, Video, Play, AlertCircle, Zap, Star,
  ChevronRight, Coins, Send, Loader2, Shield, CheckCircle2, XCircle,
  Download, Share2, Eye, ArrowLeft, Calendar, Clock, Users, Home,
  CreditCard, MessageSquare, Mic, User, LogIn
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ============== Preview Badge Component ==============
const PreviewBadge = ({ delegate, event }) => {
  if (!delegate) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No delegate selected</AlertTitle>
        <AlertDescription>Select a delegate to preview their badge.</AlertDescription>
      </Alert>
    );
  }

  const primaryColor = event?.branding?.primary_color || "#3B82F6";
  const tierInfo = delegate.tier || {};
  const eventSettings = event?.settings || {};

  // Access items with icons
  const accessItems = [
    { key: 'meals_included', label: 'Meals', icon: Utensils, enabled: eventSettings.meals_enabled },
    { key: 'housing_included', label: 'Housing', icon: Home, enabled: eventSettings.housing_enabled },
    { key: 'vip_access', label: 'VIP Access', icon: Star },
    { key: 'networking_access', label: 'Networking', icon: Users },
    { key: 'workshops_access', label: 'Workshops', icon: Calendar },
    { key: 'materials_access', label: 'Materials', icon: Download },
  ];

  return (
    <div className="max-w-md mx-auto">
      {/* Badge Card */}
      <Card className="overflow-hidden">
        <div 
          className="h-3"
          style={{ backgroundColor: primaryColor }}
        />
        <CardContent className="pt-6">
          {/* Delegate Photo/Avatar */}
          <div className="flex flex-col items-center mb-6">
            <Avatar className="h-24 w-24 mb-4">
              <AvatarFallback 
                className="text-2xl text-white"
                style={{ backgroundColor: primaryColor }}
              >
                {delegate.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
            
            <h2 className="text-2xl font-bold text-center">{delegate.name}</h2>
            {delegate.company && (
              <p className="text-slate-600">{delegate.company}</p>
            )}
            {delegate.title && (
              <p className="text-sm text-slate-500">{delegate.title}</p>
            )}
            
            {/* Tier Badge */}
            {tierInfo.name && (
              <Badge 
                className="mt-2"
                style={{ backgroundColor: primaryColor }}
              >
                {tierInfo.name}
              </Badge>
            )}
          </div>

          {/* QR Code */}
          <div className="flex justify-center mb-6">
            <div className="bg-white p-4 rounded-lg shadow-inner border">
              <QRCodeSVG 
                value={`DELEGATE:${delegate.id}:${event?.id || ''}`}
                size={150}
                level="H"
              />
            </div>
          </div>

          {/* Delegate ID */}
          <div className="text-center mb-6">
            <p className="text-xs text-slate-500">Delegate ID</p>
            <p className="font-mono text-sm">{delegate.id}</p>
          </div>

          {/* Access Permissions */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold mb-3">Access & Benefits</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {accessItems.map((item) => {
                const hasAccess = tierInfo[item.key] || false;
                const isEnabled = item.enabled !== false;
                const Icon = item.icon;
                
                if (!isEnabled) return null;
                
                return (
                  <div 
                    key={item.key}
                    className={cn(
                      "flex flex-col items-center p-2 rounded-lg text-center",
                      hasAccess ? "bg-green-50 text-green-700" : "bg-slate-50 text-slate-400"
                    )}
                  >
                    <Icon className="h-5 w-5 mb-1" />
                    <span className="text-xs">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Event Info */}
          <div className="border-t mt-4 pt-4 text-center">
            <p className="font-semibold">{event?.name}</p>
            <p className="text-sm text-slate-500">
              {event?.start_date && new Date(event.start_date).toLocaleDateString()}
              {event?.end_date && ` - ${new Date(event.end_date).toLocaleDateString()}`}
            </p>
            {event?.venue && (
              <p className="text-sm text-slate-500 flex items-center justify-center gap-1 mt-1">
                <MapPin className="h-3 w-3" />
                {event.venue}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Preview Schedule Component ==============
const PreviewSchedule = ({ eventId }) => {
  const { data: sessions, isLoading } = useQuery({
    queryKey: ['preview-sessions', eventId],
    queryFn: async () => {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/admin/events/${eventId}/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return response.json();
    },
    enabled: !!eventId
  });

  if (isLoading) {
    return <Skeleton className="h-64" />;
  }

  if (!sessions?.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="font-semibold">No Sessions Scheduled</h3>
          <p className="text-sm text-slate-500 mt-1">Sessions will appear here once added.</p>
        </CardContent>
      </Card>
    );
  }

  // Group sessions by date
  const groupedSessions = sessions.reduce((acc, session) => {
    const date = session.date || 'Unscheduled';
    if (!acc[date]) acc[date] = [];
    acc[date].push(session);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(groupedSessions).map(([date, daySessions]) => (
        <div key={date}>
          <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-indigo-600" />
            {date !== 'Unscheduled' ? new Date(date).toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric' 
            }) : 'Unscheduled'}
          </h3>
          <div className="space-y-3">
            {daySessions.map((session) => (
              <Card key={session.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium">{session.title}</h4>
                      {session.description && (
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{session.description}</p>
                      )}
                      <div className="flex flex-wrap gap-3 mt-2 text-sm text-slate-500">
                        {session.start_time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {session.start_time} - {session.end_time || 'TBD'}
                          </span>
                        )}
                        {session.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {session.location}
                          </span>
                        )}
                        {session.speaker_name && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {session.speaker_name}
                          </span>
                        )}
                      </div>
                    </div>
                    {session.type && (
                      <Badge variant="secondary">{session.type}</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== Preview Networking Component ==============
const PreviewNetworking = ({ eventId, delegates }) => {
  if (!delegates?.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <h3 className="font-semibold">No Attendees Yet</h3>
          <p className="text-sm text-slate-500 mt-1">Attendees will appear here for networking.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Connect with Attendees</CardTitle>
          <CardDescription>Network with other event participants</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {delegates.slice(0, 12).map((delegate) => (
              <Card key={delegate.id} className="hover:shadow-md transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar>
                      <AvatarFallback className="bg-indigo-100 text-indigo-600">
                        {delegate.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{delegate.name}</p>
                      {delegate.company && (
                        <p className="text-sm text-slate-500 truncate">{delegate.company}</p>
                      )}
                      {delegate.title && (
                        <p className="text-xs text-slate-400 truncate">{delegate.title}</p>
                      )}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="w-full mt-3">
                    <MessageSquare className="h-4 w-4 mr-2" /> Connect
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== Preview Payments Component ==============
const PreviewPayments = ({ delegate }) => {
  if (!delegate) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No delegate selected</AlertTitle>
        <AlertDescription>Select a delegate to preview their payment status.</AlertDescription>
      </Alert>
    );
  }

  const tierPrice = delegate.tier?.price || 0;
  const amountPaid = delegate.amount_paid || 0;
  const balance = tierPrice - amountPaid;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Payment Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold">${tierPrice.toLocaleString()}</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600">Paid</p>
              <p className="text-2xl font-bold text-green-600">${amountPaid.toLocaleString()}</p>
            </div>
            <div className={cn("p-4 rounded-lg", balance > 0 ? "bg-amber-50" : "bg-green-50")}>
              <p className={cn("text-sm", balance > 0 ? "text-amber-600" : "text-green-600")}>Balance</p>
              <p className={cn("text-2xl font-bold", balance > 0 ? "text-amber-600" : "text-green-600")}>
                ${balance.toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Payment Status */}
          <div className="mt-6 text-center">
            {balance <= 0 ? (
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Fully Paid</span>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-full">
                  <Clock className="h-5 w-5" />
                  <span className="font-medium">Payment Pending</span>
                </div>
                <Button className="w-full">
                  <CreditCard className="h-4 w-4 mr-2" /> Make Payment
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Tier Info */}
      {delegate.tier && (
        <Card>
          <CardHeader>
            <CardTitle>Your Ticket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold text-lg">{delegate.tier.name}</p>
                <p className="text-sm text-slate-500">{delegate.tier.description || 'Standard registration'}</p>
              </div>
              <Badge variant="secondary">${delegate.tier.price}</Badge>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== Main Preview Component ==============
const DelegatePreview = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const delegateIdFromUrl = searchParams.get('delegate');
  
  const [activeTab, setActiveTab] = useState("badge");
  const [selectedDelegateId, setSelectedDelegateId] = useState(delegateIdFromUrl || '');

  // Fetch event data
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['preview-event', eventId],
    queryFn: () => eventAPI.get(eventId).then(r => r.data),
    enabled: !!eventId
  });

  // Fetch delegates for selection
  const { data: delegates, isLoading: delegatesLoading } = useQuery({
    queryKey: ['preview-delegates', eventId],
    queryFn: () => adminDelegateAPI.list(eventId).then(r => r.data),
    enabled: !!eventId
  });

  // Get selected delegate
  const selectedDelegate = delegates?.find(d => d.id === selectedDelegateId);

  // Handle impersonation - login as the selected delegate
  const handleImpersonate = async (delegate) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/api/admin/impersonate/${delegate.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.delegate_token) {
        // Store admin token for later return
        localStorage.setItem('admin_original_token', token);
        localStorage.setItem('impersonating', 'true');
        localStorage.setItem('impersonated_delegate', JSON.stringify({
          id: delegate.id,
          name: delegate.name,
          email: delegate.email
        }));
        
        // Set delegate token and redirect to delegate portal
        localStorage.setItem('eventflow_token', data.delegate_token);
        localStorage.setItem('delegate', JSON.stringify(data.delegate));
        localStorage.setItem('eventId', eventId);
        
        toast.success(`Now logged in as ${delegate.name}`, {
          description: 'Click "Return to Admin" in the portal to switch back'
        });
        
        // Redirect to delegate portal
        window.location.href = '/delegate';
      } else {
        toast.error(data.detail || 'Failed to impersonate delegate');
      }
    } catch (err) {
      console.error('Impersonation error:', err);
      toast.error('Failed to impersonate delegate');
    }
  };

  if (eventLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Preview Banner */}
      <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <Eye className="h-5 w-5" />
          <span className="font-medium">Preview Mode</span>
          <span className="text-amber-100 text-sm">Viewing as attendee</span>
        </div>
        <Button 
          variant="secondary" 
          size="sm"
          onClick={() => navigate(`/admin/event/${eventId}`)}
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Admin
        </Button>
      </div>

      {/* Delegate Selector */}
      <div className="bg-white border-b px-4 py-3">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center gap-4">
          <Label className="whitespace-nowrap font-medium">Preview as:</Label>
          <Select 
            value={selectedDelegateId} 
            onValueChange={setSelectedDelegateId}
          >
            <SelectTrigger className="w-80" data-testid="delegate-selector">
              <SelectValue placeholder="Select a delegate to preview" />
            </SelectTrigger>
            <SelectContent>
              {delegates?.map((delegate) => (
                <SelectItem key={delegate.id} value={delegate.id}>
                  {delegate.name} ({delegate.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedDelegate && (
            <>
              <Badge variant="secondary">
                {selectedDelegate.tier?.name || 'No Tier'}
              </Badge>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleImpersonate(selectedDelegate)}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="impersonate-btn"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Login as Delegate
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{event?.name || 'Event Portal'}</h1>
          <p className="text-slate-600">Attendee Experience Preview</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="badge" data-testid="tab-badge">
              <User className="h-4 w-4 mr-2" /> Badge
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">
              <CreditCard className="h-4 w-4 mr-2" /> Payments
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Calendar className="h-4 w-4 mr-2" /> Schedule
            </TabsTrigger>
            <TabsTrigger value="networking" data-testid="tab-networking">
              <Users className="h-4 w-4 mr-2" /> Networking
            </TabsTrigger>
            <TabsTrigger value="safety" data-testid="tab-safety">
              <Shield className="h-4 w-4 mr-2" /> Safety
            </TabsTrigger>
            <TabsTrigger value="venue" data-testid="tab-venue">
              <MapPin className="h-4 w-4 mr-2" /> Venue
            </TabsTrigger>
          </TabsList>

          <TabsContent value="badge">
            <PreviewBadge delegate={selectedDelegate} event={event} />
          </TabsContent>

          <TabsContent value="payments">
            <PreviewPayments delegate={selectedDelegate} />
          </TabsContent>

          <TabsContent value="schedule">
            <PreviewSchedule eventId={eventId} />
          </TabsContent>

          <TabsContent value="networking">
            <PreviewNetworking eventId={eventId} delegates={delegates} />
          </TabsContent>

          <TabsContent value="safety">
            <SafetyCenter eventId={eventId} isAdmin={false} />
          </TabsContent>

          <TabsContent value="venue">
            <VenueMaps eventId={eventId} isAdmin={false} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DelegatePreview;
